import { defineStore } from 'pinia';
import axios from 'axios';
import { useAppStore } from '@/stores/useAppStore';

const API_DOMAIN = import.meta.env.VITE_API_URL;

// Notification expiry settings (in days)
const DAYS_TO_EXPIRE_READ = 14;
const DAYS_TO_EXPIRE_UNREAD = 30;

/**
 * Pinia store for managing notifications
 */
export const useNotificationStore = defineStore('notification', {
    state: () => ({
        /**
         * Array of notification objects
         * @type {Array}
         */
        notifications: [],
    }),

    getters: {
        /**
         * Returns the count of unread notifications
         * @returns {number}
         */
        unreadCount: (state) => state.notifications.filter((n) => !n.read).length,

        /**
         * Returns all notifications
         * @returns {Array}
         */
        allNotifications: (state) => state.notifications,
    },

    actions: {
        /**
         * Fetches notifications from the API and filters them based on
         * feature flags and expiry dates
         * @param {Object} payload - Request payload
         * @param {string} payload.token - Authorization token
         */
        async getNotifications(payload) {
            try {
                const response = await axios.get(`${API_DOMAIN}/notifications`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                let notifications = response.data.records || [];
                notifications = notifications.sort((a, b) => (a.createdDate < b.createdDate ? 1 : -1));
                const now = new Date().getTime() / 1000;

                const expiryOnRead = now - 60 * 60 * 24 * DAYS_TO_EXPIRE_READ;
                const expiryOnUnread = now - 60 * 60 * 24 * DAYS_TO_EXPIRE_UNREAD;

                const appStore = useAppStore();
                const userFlags = appStore.flags || {};
                const notificationFlags = {
                    new_message: 'messaging',
                };

                notifications = notifications.filter((notification) => {
                    const { read, createdDate, title } = notification;

                    const flaggedNotifications = Object.keys(notificationFlags);
                    const notificationTitle = title.toLowerCase().trim();
                    if (flaggedNotifications.includes(notificationTitle)) {
                        const flagToCheck = notificationFlags[notificationTitle];
                        return userFlags[flagToCheck];
                    }

                    if (read) {
                        return expiryOnRead < createdDate;
                    }

                    return expiryOnUnread < createdDate;
                });

                this.notifications = notifications;
            } catch (error) {
                console.log(error);
            }
        },

        /**
         * Fetches a single notification by ID
         * @param {Object} payload - Request payload
         * @param {string} payload.token - Authorization token
         * @param {string} payload.data - Notification ID
         * @returns {Promise<Object|string>} Notification data or empty string on error
         */
        async getNotification(payload) {
            try {
                const response = await axios.get(`${API_DOMAIN}/notifications/${payload.data}`, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });

                return response.data;
            } catch (error) {
                console.log(error);
            }
            return '';
        },

        /**
         * Updates a notification (typically to mark as read)
         * @param {Object} payload - Request payload
         * @param {string} payload.token - Authorization token
         * @param {Object} payload.data - Notification data
         * @param {string} payload.data.notificationId - ID of the notification to update
         * @returns {Promise<string>} Empty string
         */
        async updateNotification(payload) {
            try {
                await axios.post(`${API_DOMAIN}/notifications/${payload.data.notificationId}`, payload.data, {
                    headers: {
                        Authorization: `Bearer ${payload.token}`,
                    },
                });
            } catch (error) {
                console.log(error);
            }
            return '';
        },

        /**
         * Marks a notification as read
         * @param {Object} notification - The notification object to mark as read
         * @param {string} token - Authorization token
         * @returns {Promise<string>} Result from updateNotification
         */
        async markNotificationAsRead(notification, token) {
            const payload = {
                token,
                data: {
                    ...notification,
                    read: true,
                    notificationId: notification.notificationId,
                },
            };
            return this.updateNotification(payload);
        },
    },
});
